import * as React from 'react';
import Form, { FormInstance } from 'antd/lib/form';
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Button from 'antd/lib/button';
import Select from 'antd/lib/select';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import Tooltip from 'antd/lib/tooltip';
import { Link } from 'react-router-dom';
import { antGrey, runwayColors } from '../styles/globalStyles';
import {
    AppPlatform,
    AppStoreType,
    CompletedStepsTrigger,
    PullRequestType,
    RelativeTimeTrigger,
    SettingType,
    type TriggerCIWorkflowConditionGroup,
    TriggerCIWorkflowConditionGroupType,
} from '../../types';
import { displayTextForPullRequestType } from '../../helpers/displayHelpers';
import { createAppSettingsRouteWithSettingType } from '../../helpers/routeHelpers';
import { DEFAULT_RELATIVE_TIME_TRIGGER, RelativeTimeTriggerSettings } from '../RelativeTimeTriggerSettings';
import { CompletedStepsTriggerSettings } from '../CompletedStepsTriggerSettings';
import { Nullable } from '../../typescriptHelpers';
import { StepGraphStep } from '../../types/workflow';

const ALL_PR_TYPES = Object.values(PullRequestType);

export const conditionGroupsForTriggerCIWorkflow = (
    conditionGroups: TriggerCIWorkflowConditionGroup[] | undefined,
    relativeTimeTrigger: RelativeTimeTrigger,
    completedStepsTrigger: CompletedStepsTrigger,
): TriggerCIWorkflowConditionGroup[] =>
    conditionGroups?.length ? conditionGroups : (
        [
            {
                id: 'default',
                type: TriggerCIWorkflowConditionGroupType.EventBased,
                relativeTimeTrigger,
                completedStepsTrigger,
            },
        ]
    );

const buildNewEventBasedGroup = (): TriggerCIWorkflowConditionGroup => ({
    id: `time-${Date.now()}`,
    type: TriggerCIWorkflowConditionGroupType.EventBased,
    relativeTimeTrigger: DEFAULT_RELATIVE_TIME_TRIGGER,
});

const buildNewPRGroup = (): TriggerCIWorkflowConditionGroup => ({
    id: `pr-${Date.now()}`,
    type: TriggerCIWorkflowConditionGroupType.PullRequest,
    runwayPRTypes: [],
});

type TriggerCIWorkflowConditionGroupsProps = {
    orgId: string;
    appId: string;
    stepConfigId: Nullable<string>;
    initialGroups: TriggerCIWorkflowConditionGroup[];
    formListName?: string | (string | number)[];
    platform: AppPlatform;
    appStoreType: AppStoreType;
    stepGraphStepsMap: Record<string, StepGraphStep>;
};

export const TriggerCIWorkflowConditionGroups = ({
    orgId,
    appId,
    stepConfigId,
    initialGroups,
    formListName = 'conditionGroups',
    platform,
    appStoreType,
    stepGraphStepsMap,
}: TriggerCIWorkflowConditionGroupsProps) => {
    const parentForm = Form.useFormInstance();
    const listPath = Array.isArray(formListName) ? formListName : [formListName];
    const enabledPath = [...listPath.slice(0, -1), 'enabled'];

    const hasEventBasedGroup = (form: FormInstance) => {
        const groups: TriggerCIWorkflowConditionGroup[] = form.getFieldValue(listPath) ?? [];
        return groups.some((group) => group?.type === TriggerCIWorkflowConditionGroupType.EventBased);
    };

    return (
        <Form.List
            name={formListName}
            initialValue={initialGroups}
            rules={[
                {
                    validator: async (_, groups) => {
                        if (parentForm.getFieldValue(enabledPath) === false) {
                            return Promise.resolve();
                        }
                        if (!groups || groups.length === 0) {
                            return Promise.reject(new Error('At least one trigger condition group is required'));
                        }
                        return Promise.resolve();
                    },
                },
            ]}
        >
            {(fields, { add, remove }, { errors }) => (
                <Space direction="vertical" style={{ width: '100%' }} size={16}>
                    {fields.map((field, index) => (
                        <React.Fragment key={field.key}>
                            <Form.Item noStyle shouldUpdate>
                                {(form) => {
                                    const groupType = form.getFieldValue([...listPath, field.name, 'type']);
                                    const groupId = form.getFieldValue([...listPath, field.name, 'id']);
                                    const initialGroup = initialGroups.find((group) => group.id === groupId);

                                    return (
                                        <div
                                            style={{
                                                position: 'relative',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                padding: 20,
                                                borderRadius: 8,
                                                border: `1px solid ${antGrey.grey5}`,
                                                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                            }}
                                        >
                                            <Text strong style={{ paddingBottom: 8 }}>
                                                {groupType === TriggerCIWorkflowConditionGroupType.EventBased ?
                                                    'Event-based trigger'
                                                :   'PR-based trigger'}
                                            </Text>
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    right: 0,
                                                    paddingRight: 14,
                                                    paddingTop: 8,
                                                }}
                                            >
                                                <Button
                                                    icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                                                    danger
                                                    type="link"
                                                    onClick={() => remove(index)}
                                                />
                                            </div>
                                            <Form.Item name={[field.name, 'id']} hidden>
                                                <input type="hidden" />
                                            </Form.Item>
                                            <Form.Item name={[field.name, 'type']} hidden>
                                                <input type="hidden" />
                                            </Form.Item>
                                            <Space direction="vertical" style={{ width: '100%' }} size={12}>
                                                {groupType === TriggerCIWorkflowConditionGroupType.EventBased && (
                                                    <EventBasedGroupContent
                                                        orgId={orgId}
                                                        appId={appId}
                                                        stepConfigId={stepConfigId}
                                                        fieldName={field.name}
                                                        listPath={listPath}
                                                        initialGroup={initialGroup}
                                                    />
                                                )}
                                                {groupType === TriggerCIWorkflowConditionGroupType.PullRequest && (
                                                    <PRGroupContent fieldName={field.name} />
                                                )}
                                                <CompletedStepsSection
                                                    orgId={orgId}
                                                    appId={appId}
                                                    fieldName={field.name}
                                                    listPath={listPath}
                                                    platform={platform}
                                                    appStoreType={appStoreType}
                                                    stepGraphStepsMap={stepGraphStepsMap}
                                                />
                                            </Space>
                                        </div>
                                    );
                                }}
                            </Form.Item>
                            {index < fields.length - 1 && (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 12,
                                        color: antGrey.grey6,
                                        width: '100%',
                                    }}
                                >
                                    <div style={{ flex: 1, borderTop: `1px solid ${antGrey.grey4}` }} />
                                    <Text style={{ color: antGrey.grey6, textTransform: 'uppercase' }}>or</Text>
                                    <div style={{ flex: 1, borderTop: `1px solid ${antGrey.grey4}` }} />
                                </div>
                            )}
                        </React.Fragment>
                    ))}
                    <Form.Item noStyle shouldUpdate>
                        {(form) => {
                            const showAddEventBased = !hasEventBasedGroup(form);
                            return (
                                <Space>
                                    {showAddEventBased && (
                                        <Button
                                            icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                                            onClick={() => add(buildNewEventBasedGroup())}
                                        >
                                            Add event-based trigger
                                        </Button>
                                    )}
                                    <Button
                                        icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                                        onClick={() => add(buildNewPRGroup())}
                                    >
                                        Add PR-based trigger
                                    </Button>
                                </Space>
                            );
                        }}
                    </Form.Item>
                    <Form.ErrorList errors={errors} />
                </Space>
            )}
        </Form.List>
    );
};

const CompletedStepsSection: React.FC<{
    orgId: string;
    appId: string;
    fieldName: number;
    listPath: (string | number)[];
    platform: AppPlatform;
    appStoreType: AppStoreType;
    stepGraphStepsMap: Record<string, StepGraphStep>;
}> = ({ orgId, appId, fieldName, listPath, platform, appStoreType, stepGraphStepsMap }) => {
    const enabled = Form.useWatch([...listPath, fieldName, 'completedStepsTrigger', 'enabled']);
    const appSettingsRoute = createAppSettingsRouteWithSettingType(orgId, appId, SettingType.General);

    return (
        <>
            <Form.Item
                name={[fieldName, 'completedStepsTrigger', 'enabled']}
                valuePropName="checked"
                style={{ marginBottom: enabled ? 10 : 0 }}
            >
                <Checkbox>
                    Require previous release steps to be complete (green)
                    <Tooltip
                        title={
                            <span>
                                Only blocking steps can be included as trigger conditions. Go to{' '}
                                <Link to={appSettingsRoute} style={{ color: 'white', textDecoration: 'underline' }}>
                                    App settings
                                </Link>{' '}
                                to configure blocking and non-blocking steps.
                            </span>
                        }
                    >
                        <InfoCircleOutlined style={{ color: antGrey.grey7, marginLeft: 8 }} />
                    </Tooltip>
                </Checkbox>
            </Form.Item>
            {enabled && (
                <CompletedStepsTriggerSettings
                    orgId={orgId}
                    appId={appId}
                    platform={platform}
                    appStoreType={appStoreType}
                    stepGraphStepsMap={stepGraphStepsMap}
                    parentFieldName={[fieldName, 'completedStepsTrigger']}
                    prefixPath={listPath}
                />
            )}
        </>
    );
};

const EventBasedGroupContent: React.FC<{
    orgId: string;
    appId: string;
    stepConfigId: Nullable<string>;
    fieldName: number;
    listPath: (string | number)[];
    initialGroup?: TriggerCIWorkflowConditionGroup;
}> = ({ orgId, appId, stepConfigId, fieldName, listPath, initialGroup }) => {
    const enabled = Form.useWatch([...listPath, fieldName, 'relativeTimeTrigger', 'enabled']);

    return (
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
            <Form.Item
                name={[fieldName, 'relativeTimeTrigger', 'enabled']}
                valuePropName="checked"
                style={{ marginBottom: 0 }}
            >
                <Checkbox>
                    Eligible to run no earlier than...
                    <Tooltip title="Runway will trigger the automation at the specified target time.">
                        <InfoCircleOutlined style={{ color: antGrey.grey7, marginLeft: 8 }} />
                    </Tooltip>
                </Checkbox>
            </Form.Item>
            {enabled && (
                <RelativeTimeTriggerSettings
                    orgId={orgId}
                    appId={appId}
                    stepConfigId={stepConfigId}
                    initialValues={initialGroup?.relativeTimeTrigger ?? {}}
                    parentFieldName={[fieldName, 'relativeTimeTrigger']}
                    prefixPath={listPath}
                    isOrgLevelItem={false}
                    showAllowTriggeringBasedOnTargetDate
                />
            )}
        </Space>
    );
};

const PRGroupContent: React.FC<{
    fieldName: number;
}> = ({ fieldName }) => {
    return (
        <Form.Item
            name={[fieldName, 'runwayPRTypes']}
            label="Trigger when these PR types are merged"
            rules={[{ required: true, message: 'Select at least one PR type' }]}
            style={{ marginBottom: 0 }}
        >
            <Select
                mode="multiple"
                placeholder="Select PR types"
                style={{ width: '100%' }}
                options={ALL_PR_TYPES.map((prType) => ({
                    label: displayTextForPullRequestType(prType),
                    value: prType,
                }))}
            />
        </Form.Item>
    );
};
